import { twMerge } from "tailwind-merge";
import {
  CatalogLink,
  CatalogAutoplayButton,
  CatalogPagingButton,
} from "catalog/CatalogNavigations";
import { useParams } from "react-router-dom";
import { Image } from "./elements";
import { Anime } from "./elements";
import { AnimatePresence, motion } from "framer-motion";
import { useCatalog } from "catalog/Catalog.context";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

export default function GlobalNavigation() {
  return (
    <div className="pointer-events-none absolute left-0 top-0 z-[9999] h-full w-full">
      <NavBar />
      <Pagings />
    </div>
  );
}

function NavBar() {
  const { activePageInfo } = useCatalog();
  const { pageSlug1 } = useParams();
  const { aside } = activePageInfo;

  return (
    <AnimatePresence>
      {aside && (
        <Anime
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          className={twMerge("pointer-events-auto absolute right-0 top-0 h-full")}
        >
          <nav className={twMerge("flex h-full w-[64px] flex-col bg-white")}>
            <NavBarButton to="food/intro" theme="red" value="food">
              DELICIOUS STYLE
            </NavBarButton>
            <NavBarButton to="air/intro" theme="blue" value="air">
              FRESH STYLE
            </NavBarButton>
            <NavBarButton to="clothing/intro" theme="green" value="clothing">
              CLEAN STYLE
            </NavBarButton>
          </nav>
          <div
            className={twMerge(
              "absolute right-full top-0 mr-[-1px] h-full w-[220px] overflow-hidden",
            )}
          >
            <AnimatePresence>
              {aside?.submenu && pageSlug1 === "food" && (
                <Anime
                  initial={{ x: 220 }}
                  animate={{ x: 0 }}
                  exit={{ x: 220 }}
                  className={"absolute left-0 top-0 h-full w-full"}
                >
                  <SubMenu
                    theme="red"
                    menu={[
                      {
                        to: "food/dimchae/1",
                        label: (
                          <>
                            Kimchi <br />
                            Refrigerator
                          </>
                        ),
                        img: "/common/submenu-food-1.png",
                        value: "dimchae",
                      },
                      {
                        to: "food/praud/1",
                        label: (
                          <>
                            Premium <br />
                            Refrigerator
                          </>
                        ),
                        img: "/common/submenu-food-2.png",
                        value: "praud",
                      },
                      {
                        to: "food/mini/1",
                        label: (
                          <>
                            Retro Style <br />
                            Mini
                          </>
                        ),
                        img: "/common/submenu-food-3.png",
                        value: "mini",
                      },
                      {
                        to: "food/cooker/1",
                        label: (
                          <>
                            Rice <br />
                            Cooker
                          </>
                        ),
                        img: "/common/submenu-food-4.png",
                        value: "cooker",
                      },
                    ]}
                  />
                </Anime>
              )}
              {aside?.submenu && pageSlug1 === "air" && (
                <Anime
                  initial={{ x: 220 }}
                  animate={{ x: 0 }}
                  exit={{ x: 220 }}
                  className={"absolute left-0 top-0 h-full w-full"}
                >
                  <SubMenu
                    theme="blue"
                    menu={[
                      {
                        to: "air/purifier/1",
                        label: <>Air Purifier</>,
                        img: "/common/submenu-air-1.png",
                        value: "purifier",
                      },
                      {
                        to: "air/air-washer/1",
                        label: <>Air Washer</>,
                        img: "/common/submenu-air-2.png",
                        value: "air-washer",
                      },
                      {
                        to: "air/spowasher/1",
                        label: <>SpoWasher</>,
                        img: "/common/submenu-air-3.png",
                        value: "spowasher",
                      },
                    ]}
                  />
                </Anime>
              )}
              {aside?.submenu && pageSlug1 === "clothing" && (
                <Anime
                  initial={{ x: 220 }}
                  animate={{ x: 0 }}
                  exit={{ x: 220 }}
                  className={"absolute left-0 top-0 h-full w-full"}
                >
                  <SubMenu
                    theme="green"
                    menu={[
                      {
                        to: "clothing/mini-washer/1",
                        label: <>Mini Washer</>,
                        img: "/common/submenu-clothing-1.png",
                        imgWidth: "66",
                        value: "mini-washer",
                      },
                    ]}
                  />
                </Anime>
              )}
            </AnimatePresence>
          </div>
        </Anime>
      )}
    </AnimatePresence>
  );
}

function NavBarButton({ children, theme, to, value, ...props }) {
  const { pageSlug1 } = useParams();
  const active = pageSlug1 === value;

  const themeTextColors = {
    red: "text-[#d27273]",
    blue: "text-[#356fae]",
    green: "text-[#22917b]",
  };

  const themeAfterColors = {
    red: "after:bg-[#d27273]",
    blue: "after:bg-[#85b1d8]",
    green: "after:bg-[#65b1a2]",
  };

  const activeBackgroundColor = {
    red: "bg-[linear-gradient(to_bottom,#DA8486,#D27374)]",
    blue: "bg-[linear-gradient(to_bottom,#6F91BD,#557BA9)]",
    green: "bg-[linear-gradient(to_bottom,#3D9A8D,#1F8C81)]",
  };

  return (
    <CatalogLink
      to={to}
      className={twMerge(
        "relative flex h-full w-full items-center justify-end overflow-hidden rounded-r-lg py-[55px] uppercase",
        "after:absolute after:bottom-[25px] after:left-1/2 after:h-[4px] after:w-[70%] after:-translate-x-1/2 after:bg-black after:content-['']",
        themeTextColors[theme],
        themeAfterColors[theme],
        active && "font-bold text-white after:hidden",
        active && activeBackgroundColor[theme],
      )}
      style={{
        writingMode: "vertical-rl",
      }}
    >
      {children}
    </CatalogLink>
  );
}

function SubMenu({ menu, theme }) {
  const { pageSlug2 } = useParams();

  return (
    <nav
      className={twMerge(
        "relative flex h-full w-full flex-col border-l-[10px] border-l-[#b5b5b5] px-[20px] py-[90px] text-center text-white",
        {
          red: "bg-redGradient",
          blue: "bg-blueGradient",
          green: "bg-greenGradient",
        }[theme],
      )}
    >
      {menu.map((item) => (
        <CatalogLink
          to={item.to}
          className={twMerge(
            "flex h-[176px] flex-col items-center justify-end gap-[10px]",
            "border-b-[2px] border-y-white border-opacity-50",
            "first:border-t-[2px]",
            "pb-[10px] pt-[20px]",
          )}
        >
          <Image src={item.img} width={item.imgWidth || "60"} />
          <span className={twMerge("leading-[1.3]", pageSlug2 === item.value && "font-bold")}>
            {item.label}
          </span>
        </CatalogLink>
      ))}
    </nav>
  );
}

function Pagings() {
  const { activePageInfo, isFirstPage, isLastPage } = useCatalog();

  if (!activePageInfo) return null;

  const { pagingTheme, aside } = activePageInfo;

  const width = !aside
    ? "calc(100% - 0px)"
    : !aside.submenu
      ? "calc(100% - 64px)"
      : "calc(100% - 284px)";

  return (
    <Anime
      initial={{ width }}
      animate={{ width }}
      className={twMerge(
        "absolute bottom-0 left-0 flex w-full justify-between text-4xl text-neutral-400",
        "px-[15px] py-[15px]",
        pagingTheme === "white" && "text-white",
      )}
    >
      <CatalogPagingButton
        direction="prev"
        className={twMerge(
          "pointer-events-auto flex h-[60px] w-[60px] items-center justify-center",
          isFirstPage && "opacity-0",
        )}
      >
        <FaChevronLeft />
      </CatalogPagingButton>
      <CatalogPagingButton
        direction="next"
        className={twMerge(
          "pointer-events-auto flex h-[60px] w-[60px] items-center justify-center",
          isLastPage && "opacity-0",
        )}
      >
        <FaChevronRight />
      </CatalogPagingButton>
    </Anime>
  );
}
